import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HighchartsReact from 'highcharts-react-official';
import jsPDF from 'jspdf';
import prismLogo from '../assets/prism-logo.svg';
import { MonthlyClosingsChart } from './MonthlyClosingsChart';
import { MillionDollarMarketChart } from './MillionDollarMarketChart';
import { SalesPricesChart } from './SalesPricesChart';
import { SalesPSFChart } from './SalesPSFChart';
import { SalesPriceDistributionChart } from './SalesPriceDistributionChart';
import { SalesDistributionChart } from './SalesDistributionChart';
import { LuxVsTraditionalChart } from './LuxVsTraditionalChart';
import { PriceIndexChart } from './PriceIndexChart';
import { ShadowInventoryChart } from './ShadowInventoryChart';
import { useUserContext } from '../context/UserContext';

interface ChartData {
  categories: string[];
  years?: number[];
  series: { name: string; data: (number | null)[] }[];
  buildingIds?: number[];
}

interface BuildingOpening {
  name: string;
  firstSale: number;
  avgPrice: number | null;
}

interface TimePeriod {
  id: number;
  name: string;
  startYear: number;
  startQuarter: number;
  endYear: number;
  endQuarter: number;
}

interface Props {
  chartName: string;
  chartTitle: string;
  neighborhoodId: number;
  neighborhoodName: string;
  propertyTypeLabel: string;
  buildingCategoryLabel?: string;
  data: ChartData;
  buildingOpenings?: BuildingOpening[];
  timePeriods?: TimePeriod[];
  orgLogoUrl?: string;
  onAddToPackage?: (hiddenSeries: string[]) => void;
}

const PRISM_LOGO_ASPECT = 1179 / 375.935;

// `null` in a series (as opposed to `0`) is the codebase's existing convention
// for "no data", paired with `connectNulls: false` on every line chart so it
// renders as a visible break rather than a bridged line.
function findGapCategories(data: ChartData): string[] {
  const missing = new Set<number>();
  for (const s of data.series) {
    s.data.forEach((v, i) => { if (v == null) missing.add(i); });
  }
  return Array.from(missing).sort((a, b) => a - b).map(i => data.categories[i]).filter(Boolean);
}

function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function fetchSvgAsDataUrl(svgSrc: string, pixW: number, pixH: number): Promise<string | null> {
  try {
    const res = await fetch(svgSrc);
    let text = await res.text();
    text = text.replace(/<svg\b/, `<svg width="${pixW}" height="${pixH}"`);
    const blob = new Blob([text], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);
    const c = document.createElement('canvas');
    c.width = pixW; c.height = pixH;
    c.getContext('2d')!.drawImage(img, 0, 0);
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

async function svgToPDF(container: HTMLDivElement, filename: string, orgLogoUrl?: string, userName?: string) {
  const svgEl = container.querySelector('svg');
  if (!svgEl) return;

  const width = svgEl.clientWidth || svgEl.viewBox.baseVal.width;
  const height = svgEl.clientHeight || svgEl.viewBox.baseVal.height;
  const scale = 2;

  const svgString = new XMLSerializer().serializeToString(svgEl);
  const blobUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));
  const chartImg = await loadImage(blobUrl);
  URL.revokeObjectURL(blobUrl);

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.drawImage(chartImg, 0, 0);

  const imgData = canvas.toDataURL('image/png');

  const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;
  const availW = pageW - 2 * marginX;
  const availH = pageH - marginTop - marginBot;
  const widthIn = width / 96;
  const heightIn = height / 96;
  const fit = Math.min(availW / widthIn, availH / heightIn);
  const imgW = widthIn * fit;
  const imgH = heightIn * fit;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });

  // Chart centered in available area
  pdf.addImage(imgData, 'PNG', (pageW - imgW) / 2, marginTop + (availH - imgH) / 2, imgW, imgH);

  // Org logo: top-left, 0.5" from edge, 0.5" tall
  if (orgLogoUrl) {
    const logoImg = await loadImage(orgLogoUrl, true).catch(() => null);
    if (logoImg) {
      const logoH = 0.5;
      const logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH;
      const c = document.createElement('canvas');
      c.width = logoImg.naturalWidth; c.height = logoImg.naturalHeight;
      c.getContext('2d')!.drawImage(logoImg, 0, 0);
      pdf.addImage(c.toDataURL('image/png'), 'PNG', 0.5, 0.5, logoW, logoH);
    }
  }

  // Prism logo: bottom-right, 0.5" from edge, 0.5" tall
  const prismPixH = 200;
  const prismPixW = Math.round(PRISM_LOGO_ASPECT * prismPixH);
  const prismData = await fetchSvgAsDataUrl(prismLogo as string, prismPixW, prismPixH);
  if (prismData) {
    const prismH = 0.5;
    const prismW = PRISM_LOGO_ASPECT * prismH;
    pdf.addImage(prismData, 'PNG', pageW - prismW - 0.5, pageH - prismH - 0.5, prismW, prismH);
  }

  if (userName) {
    const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(160, 160, 160);
    pdf.text(`Prism Insight produced for ${userName} on ${date}`, 0.5, pageH - 0.75);
  }

  pdf.save(`${filename}.pdf`);
}

export function ChartRenderer({ chartName, chartTitle, neighborhoodName, propertyTypeLabel, buildingCategoryLabel, data, buildingOpenings, timePeriods, orgLogoUrl, onAddToPackage }: Props) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { showPdfLens, me } = useUserContext();
  const navigate = useNavigate();
  const filename = `${chartTitle} — ${neighborhoodName} — ${propertyTypeLabel}`;
  const userName = me?.displayName?.trim() || me?.email || undefined;

  const gapCategories = findGapCategories(data);
  const footnote = gapCategories.length > 0 ? `* No data available for: ${gapCategories.join(', ')}` : '';

  // Applied imperatively via the shared chart ref rather than threaded through
  // each chart component's own Options object, so every chart type picks up
  // gap footnotes for free and the caption survives into the PDF export
  // (svgToPDF captures the chart's rendered <svg>, which includes it).
  useEffect(() => {
    chartRef.current?.chart?.update({
      caption: {
        text: footnote,
        align: 'left',
        style: { fontFamily: "'Montserrat Light', 'Montserrat', sans-serif", fontSize: '9pt', color: '#999999' }
      }
    });
  }, [footnote]);

  const downloadPDF = () => {
    if (containerRef.current) svgToPDF(containerRef.current, filename, orgLogoUrl, userName);
  };

  let chart: React.ReactElement | null = null;
  switch (chartName) {
    case 'monthlyClosings':
      chart = <MonthlyClosingsChart ref={chartRef} data={data as any} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} />;
      break;
    case 'millionDollarMarket':
      chart = <MillionDollarMarketChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} />;
      break;
    case 'salesPrices':
      chart = <SalesPricesChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} buildingCategoryLabel={buildingCategoryLabel} buildingOpenings={buildingOpenings} />;
      break;
    case 'salesPSF':
      chart = <SalesPSFChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} buildingCategoryLabel={buildingCategoryLabel} buildingOpenings={buildingOpenings} />;
      break;
    case 'salesPriceDistribution':
      chart = <SalesPriceDistributionChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} />;
      break;
    case 'salesDistribution':
      chart = <SalesDistributionChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} />;
      break;
    case 'luxVsTraditional':
      chart = <LuxVsTraditionalChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} />;
      break;
    case 'priceIndex':
      chart = <PriceIndexChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} propertyTypeLabel={propertyTypeLabel} timePeriods={timePeriods} />;
      break;
    case 'shadowInventory':
      chart = <ShadowInventoryChart ref={chartRef} data={data} title={chartTitle} neighborhoodName={neighborhoodName} buildingCategoryLabel={buildingCategoryLabel} buildingIds={data.buildingIds} onBuildingClick={id => navigate(`/building/${id}`)} />;
      break;
    default:
      return <p style={{ color: '#999' }}>Unknown chart type: {chartName}</p>;
  }

  return (
    <div>
      {showPdfLens && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.5rem' }}>
          {onAddToPackage && (
            <button
              onClick={() => {
                const chart = chartRef.current?.chart;
                const hidden = chart?.series.filter((s: any) => !s.visible).map((s: any) => s.name as string) ?? [];
                onAddToPackage(hidden);
              }}
              style={{
                padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer',
                border: '1px solid #ddd', borderRadius: 6, background: '#fff',
              }}
            >
              + Add to Lens
            </button>
          )}
          <button
            onClick={downloadPDF}
            style={{
              padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer',
              border: '1px solid #ddd', borderRadius: 6, background: '#fff'
            }}
          >
            Download PDF
          </button>
        </div>
      )}
      <div ref={containerRef}>
        {chart}
      </div>
    </div>
  );
}

interface ChartDescriptionModalProps {
  title: string;
  description: string;
  onClose: () => void;
}

export function ChartDescriptionModal({ title, description, onClose }: ChartDescriptionModalProps) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{ width: 440, maxWidth: '90vw', background: '#fff', borderRadius: 10, padding: '1.75rem', fontFamily: "'Montserrat', sans-serif" }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 0.75rem' }}>
          {title}
        </h3>
        <p style={{ fontSize: 14, color: '#444', margin: '0 0 1.25rem', whiteSpace: 'pre-wrap' }}>
          {description}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '0.55rem 1.1rem', fontSize: 13, fontWeight: 600, background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
